// Единая точка чтения конфигурации из .env. Нигде в коде нет хардкода адресов,
// токенов и аудио-параметров — только через этот модуль.

// Чем приложение говорит с агентом:
//   mock   → встроенная заглушка (без сервера), для проверки UI.
//   bridge → HTTP-мост agent-bridge (текст → агент OpenClaw → текст). Голос на Siri.
//   ws     → прямой WebSocket-бэкенд со стримом аудио (исходный режим).
export type BackendMode = 'mock' | 'bridge' | 'ws'
// Распознавание речи (моя речь → текст):
//   browser → встроенное в браузер/iPhone (Siri), бесплатно, на устройстве.
//   off     → только текстовый ввод.
export type SttMode = 'browser' | 'off'
export type AuthMode = 'none' | 'header' | 'query'
export type AudioSendFormat = 'pcm16' | 'base64'
export type AudioRecvFormat = 'pcm16' | 'encoded'
// Откуда брать голос агента:
//   backend → проигрывать только аудио, присланное сервером (его TTS).
//   browser → всегда озвучивать текст ответа локальным синтезом браузера.
//   auto    → если сервер прислал аудио — играть его; если только текст — озвучить браузером.
//   off     → не озвучивать (только текст на экране).
export type TtsMode = 'backend' | 'browser' | 'auto' | 'off'

function bool(v: string | undefined, fallback = false): boolean {
  if (v == null) return fallback
  return v.toLowerCase() === 'true' || v === '1'
}

function num(v: string | undefined, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

function float(v: string | undefined, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

const env = import.meta.env

export interface AppConfig {
  backend: BackendMode
  useMock: boolean
  apiBaseUrl: string
  agentPath: string
  wsUrl: string
  agentStatusPath: string
  agentStatusPollMs: number
  stt: {
    mode: SttMode
    lang: string
  }
  auth: {
    token: string
    mode: AuthMode
    header: string
    scheme: string
    queryParam: string
  }
  audio: {
    sampleRate: number
    frameSize: number
    sendFormat: AudioSendFormat
    recvFormat: AudioRecvFormat
  }
  tts: {
    mode: TtsMode
    lang: string
    rate: number
    pitch: number
  }
}

// Режим бэкенда: явный VITE_BACKEND имеет приоритет; иначе — из VITE_USE_MOCK
// (обратная совместимость).
const backendMode: BackendMode =
  (env.VITE_BACKEND as BackendMode) || (bool(env.VITE_USE_MOCK, true) ? 'mock' : 'ws')

// Пустая строка = same-origin (запросы на свой же домен, через Pages-функцию).
// Поэтому отличаем «не задано» (undefined → дефолт) от «задано пустым» ('' → same-origin).
const rawApiBase = env.VITE_API_BASE_URL
const apiBase = rawApiBase == null ? 'http://localhost:8787' : rawApiBase.replace(/\/+$/, '')

export const config: AppConfig = {
  backend: backendMode,
  useMock: backendMode === 'mock',
  apiBaseUrl: apiBase,
  agentPath: env.VITE_AGENT_PATH || '/api/agent',
  wsUrl: env.VITE_WS_URL || 'ws://51.210.8.149/voice',
  agentStatusPath: env.VITE_AGENT_STATUS_PATH || '/agent/status',
  agentStatusPollMs: num(env.VITE_AGENT_STATUS_POLL_MS, 3000),
  stt: {
    mode: (env.VITE_STT_MODE as SttMode) || 'browser',
    lang: env.VITE_STT_LANG || 'ru-RU',
  },
  auth: {
    token: env.VITE_AUTH_TOKEN || '',
    mode: (env.VITE_AUTH_MODE as AuthMode) || 'none',
    header: env.VITE_AUTH_HEADER || 'Authorization',
    scheme: env.VITE_AUTH_SCHEME || 'Bearer',
    queryParam: env.VITE_AUTH_QUERY_PARAM || 'token',
  },
  audio: {
    sampleRate: num(env.VITE_AUDIO_SAMPLE_RATE, 16000),
    frameSize: num(env.VITE_AUDIO_FRAME_SIZE, 2048),
    sendFormat: (env.VITE_AUDIO_SEND_FORMAT as AudioSendFormat) || 'pcm16',
    recvFormat: (env.VITE_AUDIO_RECV_FORMAT as AudioRecvFormat) || 'pcm16',
  },
  tts: {
    mode: (env.VITE_TTS_MODE as TtsMode) || 'auto',
    lang: env.VITE_TTS_LANG || 'ru-RU',
    rate: float(env.VITE_TTS_RATE, 1),
    pitch: float(env.VITE_TTS_PITCH, 1),
  },
}

// Строит URL к мосту. При пустом apiBaseUrl — относительный путь (same-origin).
function buildUrl(path: string): string {
  if (!config.apiBaseUrl) {
    // same-origin: относительный путь (+ query-токен, если задан)
    if (config.auth.mode === 'query' && config.auth.token) {
      return `${path}?${config.auth.queryParam}=${encodeURIComponent(config.auth.token)}`
    }
    return path
  }
  const url = new URL(config.apiBaseUrl + path)
  if (config.auth.mode === 'query' && config.auth.token) {
    url.searchParams.set(config.auth.queryParam, config.auth.token)
  }
  return url.toString()
}

/** URL HTTP-моста агента (POST текста). */
export function agentUrl(): string {
  return buildUrl(config.agentPath)
}

/** REST URL статуса агента. */
export function agentStatusUrl(): string {
  return buildUrl(config.agentStatusPath)
}

/** Заголовки для REST-запросов с учётом header-аутентификации. */
export function authHeaders(): Record<string, string> {
  if (config.auth.mode === 'header' && config.auth.token) {
    const value = config.auth.scheme
      ? `${config.auth.scheme} ${config.auth.token}`
      : config.auth.token
    return { [config.auth.header]: value }
  }
  return {}
}

/**
 * WS URL с query-аутентификацией. В браузере к WebSocket нельзя добавить
 * заголовки, поэтому при auth.mode === 'header' токен всё равно кладётся в query
 * (бэкенд должен это поддерживать) — см. README.
 */
export function wsUrlWithAuth(): string {
  const url = new URL(config.wsUrl)
  if (config.auth.token && config.auth.mode !== 'none') {
    url.searchParams.set(config.auth.queryParam, config.auth.token)
  }
  return url.toString()
}
